import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../api';
import GlassCard from './ui/GlassCard';
import Badge from './ui/Badge';
import Btn from './ui/Btn';
import Field from './ui/Field';
import ConfirmDialog from './ui/ConfirmDialog';
import EmptyState from './ui/EmptyState';
import SearchBar from './ui/SearchBar';
import Header from './ui/Header';
import InfoTable from './ui/InfoTable';
import Icon from './ui/Icon';
import { formatDate } from '../utils/format';
import PhotoSlider from './ui/PhotoSlider';
import AutocompleteInput from './ui/AutocompleteInput';

interface Chip {
  id: number;
  name: string;
  description?: string;
  package?: string;
  quantity: number;
  section?: string;
  cell?: string;
  shadow_section?: string;
  shadow_cell?: string;
  is_shadowed?: number;     // 0 | 1 из БД
  shadow?: boolean;         // удобный alias
  position?: string;
  previous_position?: string;
  created_at?: string;
  photo_path?: string;
}

interface ChipForm {
  name: string;
  description: string;
  package: string;
  quantity: number | string;
  section: string;
  cell: string;
}

interface FormErrors {
  name?: string;
  quantity?: string;
  section?: string;
}

type View = 'list' | 'detail' | 'form' | 'inventory';

// Где остановились в прошлый раз; чистится, когда склад обошли до конца.
const INVENTORY_PROGRESS_KEY = 'inventoryProgress';

const ChipsSection: React.FC = () => {
  const [chips, setChips] = useState<Chip[]>([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<View>('list');
  const [selected, setSelected] = useState<Chip | null>(null);
  const [search, setSearch] = useState('');
  const [showShadow, setShowShadow] = useState(false);
  const [lowQtyFilter, setLowQtyFilter] = useState<number | null>(null);
  const [lowQtyInput, setLowQtyInput] = useState('5');
  const [confirm, setConfirm] = useState(false);
  const [form, setForm] = useState<ChipForm>({ name: '', description: '', package: '', quantity: 0, section: '', cell: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [descriptions, setDescriptions] = useState<{ id: number; value: string }[]>([]);
  const [packages, setPackages] = useState<{ id: number; value: string }[]>([]);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Inventory state
  const [invChips, setInvChips] = useState<Chip[]>([]);
  const [invLoading, setInvLoading] = useState(false);
  const [invIndex, setInvIndex] = useState(0);
  const [invDrafts, setInvDrafts] = useState<Record<number, ChipForm>>({});
  const [invError, setInvError] = useState<string | null>(null);
  const [invConfirmExit, setInvConfirmExit] = useState(false);
  const [invSaving, setInvSaving] = useState(false);
  const [invDone, setInvDone] = useState(false);

  const loadChips = useCallback(async () => {
    setLoading(true);
    try {
      if (showShadow) {
        const res = await api.getShadowChips();
        setChips(res.data);
      } else {
        const res = await api.getChips();
        setChips(res.data);
      }
    } catch (err) {
      console.error('Error loading chips', err);
    } finally {
      setLoading(false);
    }
  }, [showShadow]);

  useEffect(() => {
    loadChips();
  }, [loadChips]);

  useEffect(() => {
    api.getChipDescriptions().then(r =>
      setDescriptions(r.data.map((d: any) => ({ id: d.id, value: d.description })))
    ).catch(() => {});
    api.getChipPackages().then(r =>
      setPackages(r.data.map((p: any) => ({ id: p.id, value: p.package })))
    ).catch(() => {});
  }, []);

  const displayed = useMemo(() =>
    chips.filter(c => {
      const q = search.toLowerCase();
      const matchSearch = !q || c.name.toLowerCase().includes(q) || (c.package || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q);
      const matchQty = lowQtyFilter === null || c.quantity < lowQtyFilter;
      return matchSearch && matchQty;
    }),
    [chips, search, lowQtyFilter]
  );

  const openAdd = async () => {
    setForm({ name: '', description: '', package: '', quantity: 0, section: '', cell: '' });
    setErrors({});
    setSelected(null);
    setPhotoFile(null);
    setPhotoPreview(null);
    setView('form');
    try {
      const res = await api.getNextPosition();
      const { section, cell } = res.data;
      setForm(f => ({ ...f, section: section || '', cell: cell || '' }));
    } catch {}
  };

  const openEdit = (chip: Chip) => {
    setForm({
      name: chip.name,
      description: chip.description || '',
      package: chip.package || '',
      quantity: chip.quantity,
      section: chip.section || '',
      cell: chip.cell || '',
    });
    setErrors({});
    setSelected(chip);
    setPhotoFile(null);
    setPhotoPreview(chip.photo_path
      ? (chip.photo_path.startsWith('http') ? chip.photo_path : chip.photo_path)
      : null);
    setView('form');
  };

  const save = async () => {
    const err: FormErrors = {};

    // 1. Название: обязательное + уникальность
    if (!form.name.trim()) {
      err.name = 'Поле обязательно';
    } else {
      const nameExists = chips.some(c =>
        c.name.toLowerCase() === form.name.trim().toLowerCase() &&
        (!selected || c.id !== selected.id)
      );
      if (nameExists) err.name = 'Микросхема с таким названием уже существует';
    }

    // 2. Количество: только целое число ≥ 0, без запятых
    const qtyStr = String(form.quantity);
    if (qtyStr.includes(',') || qtyStr.includes('.')) {
      err.quantity = 'Только целое число (без запятых и точек)';
    } else {
      const qty = Number(qtyStr);
      if (qtyStr.trim() === '' || isNaN(qty) || !Number.isInteger(qty) || qty < 0) {
        err.quantity = 'Введите целое число ≥ 0';
      } else if (qty > 9999) {
        err.quantity = 'Максимум 9999 шт.';
      }
    }

    // 3. Ячейка: проверка занятости
    if (form.section && form.cell) {
      const positionTaken = chips.some(c =>
        String(c.section) === String(form.section).trim() &&
        String(c.cell) === String(form.cell).trim() &&
        (!selected || c.id !== selected.id)
      );
      if (positionTaken) err.section = `Ячейка ${form.section}|${form.cell} уже занята другой микросхемой`;
    }

    if (Object.keys(err).length) { setErrors(err); return; }

    setSaving(true);
    try {
      const buildFormData = () => {
        const fd = new FormData();
        fd.append('name', form.name);
        fd.append('description', form.description);
        fd.append('package', form.package);
        fd.append('section', form.section);
        fd.append('cell', form.cell);
        fd.append('quantity', String(Number(form.quantity)));
        if (photoFile) fd.append('photo', photoFile);
        else if (selected?.photo_path) fd.append('photo_path', selected.photo_path);
        return fd;
      };

      if (selected) {
        const res = await api.updateChip(selected.id, buildFormData());
        const updated: Chip = {
          ...selected,
          name: form.name,
          description: form.description || undefined,
          package: form.package || undefined,
          quantity: Number(form.quantity),
          section: form.section || undefined,
          cell: form.cell || undefined,
          position: form.section && form.cell ? `${form.section} | ${form.cell}` : undefined,
          photo_path: res.data?.photo_path || selected.photo_path,
        };
        setChips(cs => cs.map(c => c.id === updated.id ? updated : c));
        setSelected(updated);
        setView('detail');
      } else {
        const res = await api.createChip(buildFormData());
        const newChip: Chip = {
          id: res.data.id,
          name: form.name,
          description: form.description || undefined,
          package: form.package || undefined,
          quantity: Number(form.quantity),
          section: form.section || undefined,
          cell: form.cell || undefined,
          position: form.section && form.cell ? `${form.section} | ${form.cell}` : undefined,
          photo_path: res.data?.photo_path || undefined,
          created_at: new Date().toISOString(),
        };
        setChips(cs => [newChip, ...cs]);
        setView('list');
      }

      // Refresh autocomplete lists in case new description/package was added
      api.getChipDescriptions().then(r =>
        setDescriptions(r.data.map((d: any) => ({ id: d.id, value: d.description })))
      ).catch(() => {});
      api.getChipPackages().then(r =>
        setPackages(r.data.map((p: any) => ({ id: p.id, value: p.package })))
      ).catch(() => {});
    } catch (err) {
      console.error('Save error', err);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!selected) return;
    try {
      await api.deleteChip(selected.id);
      setChips(cs => cs.filter(c => c.id !== selected.id));
      setView('list');
      setConfirm(false);
    } catch (err) {
      console.error('Delete error', err);
    }
  };

  const changeQty = async (delta: number) => {
    if (!selected) return;
    try {
      const res = await api.updateChipQuantity(selected.id, delta);
      const raw = res.data;
      // PATCH возвращает сырую строку БД без position — восстанавливаем
      const updated: Chip = {
        ...raw,
        position: raw.section && raw.cell ? `${raw.section} | ${raw.cell}` : selected.position,
      };
      setChips(cs => cs.map(c => c.id === updated.id ? updated : c));
      setSelected(updated);
    } catch (err) {
      console.error('Qty error', err);
    }
  };

  const deleteDescription = async (id: number) => {
    try {
      await api.deleteChipDescription(id);
      setDescriptions(ds => ds.filter(d => d.id !== id));
    } catch {}
  };

  const deletePackage = async (id: number) => {
    try {
      await api.deleteChipPackage(id);
      setPackages(ps => ps.filter(p => p.id !== id));
    } catch {}
  };

  const toggleShadow = async () => {
    if (!selected) return;
    const isShadowed = selected.shadow || selected.is_shadowed === 1;
    try {
      if (isShadowed) {
        const next = await api.getNextPosition();
        const section = next.data?.section || '1';
        const cell = next.data?.cell || '1';
        await api.restoreChip(selected.id, { section, cell });
      } else {
        await api.shadowChip(selected.id);
      }
      await loadChips();
      setView('list');
    } catch (err: any) {
      console.error('Shadow toggle error', err?.response?.data || err);
    }
  };

  const chipToDraft = (c: Chip): ChipForm => ({
    name: c.name || '',
    description: c.description || '',
    package: c.package || '',
    quantity: String(c.quantity ?? 0),
    section: c.section || '',
    cell: c.cell || '',
  });

  const draftFor = (c: Chip): ChipForm => invDrafts[c.id] ?? chipToDraft(c);

  const isDraftChanged = (c: Chip) => {
    const draft = invDrafts[c.id];
    if (!draft) return false;
    const original = chipToDraft(c);
    return (Object.keys(original) as (keyof ChipForm)[])
      .some(key => String(draft[key]) !== String(original[key]));
  };

  const validateDraft = (draft: ChipForm): string | null => {
    if (!String(draft.name).trim()) return 'Укажите наименование';
    const raw = String(draft.quantity);
    if (raw.includes(',') || raw.includes('.')) return 'Количество — целое число без запятых';
    const qty = Number(raw);
    if (!raw.trim() || isNaN(qty) || !Number.isInteger(qty) || qty < 0) return 'Количество — целое число ≥ 0';
    if (qty > 9999) return 'Максимум 9999 шт.';
    return null;
  };

  const setDraftField = (id: number, chip: Chip, field: keyof ChipForm, value: string) => {
    setInvError(null);
    setInvDrafts(prev => ({
      ...prev,
      [id]: { ...(prev[id] ?? chipToDraft(chip)), [field]: value },
    }));
  };

  const openInventory = async () => {
    // Считаем всегда основной склад: из теневого списка инвентаризация приходила пустой,
    // потому что наследовала его выборку.
    setInvDone(false);
    setInvDrafts({});
    setInvError(null);
    setView('inventory');
    setInvLoading(true);
    try {
      const list: Chip[] = showShadow ? (await api.getChips()).data : chips;
      setInvChips(list);
      // Продолжаем с той позиции, где в прошлый раз нажали «Сохранить».
      const saved = Number(localStorage.getItem(INVENTORY_PROGRESS_KEY) || 0);
      setInvIndex(saved > 0 && saved < list.length ? saved : 0);
    } catch (err) {
      console.error('Error loading inventory', err);
      setInvChips([]);
    } finally {
      setInvLoading(false);
    }
  };

  // finished === true: обошли весь склад, в следующий раз начинаем сначала.
  const saveInventory = async (finished: boolean) => {
    const changed = invChips.filter(isDraftChanged);
    const broken = changed.find(c => validateDraft(draftFor(c)) !== null);
    if (broken) {
      setInvIndex(invChips.indexOf(broken));
      setInvError(validateDraft(draftFor(broken)));
      return;
    }

    setInvSaving(true);
    try {
      for (const chip of changed) {
        const draft = draftFor(chip);
        const fd = new FormData();
        fd.append('name', draft.name);
        fd.append('description', draft.description);
        fd.append('package', draft.package);
        fd.append('section', draft.section);
        fd.append('cell', draft.cell);
        fd.append('quantity', String(Number(draft.quantity)));
        // Без photo_path бэкенд затрёт фото компонента.
        if (chip.photo_path) fd.append('photo_path', chip.photo_path);
        await api.updateChip(chip.id, fd);
      }

      if (finished) {
        localStorage.removeItem(INVENTORY_PROGRESS_KEY);
      } else {
        localStorage.setItem(INVENTORY_PROGRESS_KEY, String(invIndex));
      }

      await loadChips();
      api.getChipDescriptions().then(r =>
        setDescriptions(r.data.map((d: any) => ({ id: d.id, value: d.description })))
      ).catch(() => {});
      api.getChipPackages().then(r =>
        setPackages(r.data.map((p: any) => ({ id: p.id, value: p.package })))
      ).catch(() => {});

      setInvChips([]);
      setInvDrafts({});
      setInvDone(true);
      setTimeout(() => setView('list'), 1200);
    } catch (err) {
      console.error('Inventory save error', err);
      setInvError('Не удалось сохранить, попробуйте ещё раз');
    } finally {
      setInvSaving(false);
    }
  };

  const exitInventory = () => {
    if (invChips.some(isDraftChanged)) {
      setInvConfirmExit(true);
      return;
    }
    setView('list');
  };

  const PillBtn: React.FC<{
    active: boolean;
    amber?: boolean;
    onClick: () => void;
    children: React.ReactNode;
  }> = ({ active, amber, onClick, children }) => (
    <button
      onClick={onClick}
      style={{
        padding: '6px 16px',
        borderRadius: 100,
        border: '1px solid',
        fontSize: 13,
        fontFamily: 'var(--sans)',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.15s',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        background: active ? (amber ? 'var(--amber)' : 'var(--teal-solid)') : 'var(--glass-bg)',
        color: active ? '#0d1117' : 'var(--text2)',
        borderColor: active ? (amber ? 'var(--amber)' : 'var(--teal)') : 'var(--glass-border)',
      }}
    >
      {children}
    </button>
  );

  if (view === 'inventory') {
    const chip = invChips[invIndex];
    const draft = chip ? draftFor(chip) : null;
    const changedCount = invChips.filter(isDraftChanged).length;
    const isLast = invIndex >= invChips.length - 1;
    const qtyDelta = chip && draft && !validateDraft(draft)
      ? Number(draft.quantity) - chip.quantity
      : 0;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {invConfirmExit && (
          <ConfirmDialog
            message={`Выйти без сохранения? Правок: ${changedCount}.`}
            onConfirm={() => { setInvConfirmExit(false); setView('list'); }}
            onCancel={() => setInvConfirmExit(false)}
            confirmLabel="Выйти"
          />
        )}
        <Header title="Инвентаризация" onBack={exitInventory} />

        {invLoading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text2)' }}>
            Загрузка...
          </div>
        ) : invDone ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
            <div style={{ fontSize: 48 }}>✅</div>
            <div style={{ color: 'var(--teal)', fontWeight: 700, fontSize: 18 }}>Инвентаризация сохранена</div>
          </div>
        ) : !chip || !draft ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EmptyState icon="chips" title="Нечего считать" sub="На складе нет компонентов" />
          </div>
        ) : (
          <>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--glass-border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--text1)', fontWeight: 700 }}>
                  {invIndex + 1} / {invChips.length}
                </span>
                {changedCount > 0 && (
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--teal)' }}>
                    изменено: {changedCount}
                  </span>
                )}
              </div>
              <div style={{ height: 4, borderRadius: 100, background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${((invIndex + 1) / invChips.length) * 100}%`,
                  background: 'var(--teal-solid)',
                  transition: 'width 0.2s',
                }} />
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 100px' }}>
              <GlassCard style={{ padding: 16, borderColor: isDraftChanged(chip) ? 'var(--teal)' : undefined }}>
                {chip.photo_path && <PhotoSlider images={[chip.photo_path]} />}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <Field label="Наименование" required>
                    <input
                      value={draft.name}
                      onChange={e => setDraftField(chip.id, chip, 'name', e.target.value)}
                      placeholder="Название"
                    />
                  </Field>
                  <Field label="Описание">
                    <AutocompleteInput
                      value={draft.description}
                      onChange={v => setDraftField(chip.id, chip, 'description', v)}
                      suggestions={descriptions}
                      onDelete={deleteDescription}
                      placeholder="Характеристики..."
                    />
                  </Field>
                  <Field label="Корпус">
                    <AutocompleteInput
                      value={draft.package}
                      onChange={v => setDraftField(chip.id, chip, 'package', v)}
                      suggestions={packages}
                      onDelete={deletePackage}
                      placeholder="DIP-8, SOIC-16..."
                    />
                  </Field>
                  <Field label={`Кол-во · по учёту ${chip.quantity}`}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="number"
                        min="0"
                        max="9999"
                        step="1"
                        value={draft.quantity}
                        onChange={e => {
                          const v = e.target.value;
                          if (v.includes('.') || v.includes(',')) return;
                          setDraftField(chip.id, chip, 'quantity', v);
                        }}
                        onKeyDown={e => { if (e.key === ',' || e.key === '.') e.preventDefault(); }}
                        style={{ flex: 1 }}
                      />
                      {qtyDelta !== 0 && (
                        <span style={{
                          fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 15,
                          color: qtyDelta > 0 ? 'var(--teal)' : 'var(--red)',
                          minWidth: 44, textAlign: 'center',
                        }}>
                          {qtyDelta > 0 ? `+${qtyDelta}` : qtyDelta}
                        </span>
                      )}
                    </div>
                  </Field>
                  <Field label="Позиция">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                      <input
                        value={draft.section}
                        onChange={e => setDraftField(chip.id, chip, 'section', e.target.value)}
                        placeholder="Секция"
                        style={{ borderRadius: 'var(--r-sm) 0 0 var(--r-sm)', borderRight: 'none', flex: 1 }}
                      />
                      <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--glass-border)', flexShrink: 0 }} />
                      <input
                        value={draft.cell}
                        onChange={e => setDraftField(chip.id, chip, 'cell', e.target.value)}
                        placeholder="Ячейка"
                        style={{ borderRadius: '0 var(--r-sm) var(--r-sm) 0', borderLeft: 'none', flex: 1 }}
                      />
                    </div>
                  </Field>
                </div>
              </GlassCard>

              {invError && (
                <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 'var(--r-sm)', background: 'var(--red-dim)', border: '1px solid var(--red-mid)', color: 'var(--red)', fontSize: 13 }}>
                  {invError}
                </div>
              )}
            </div>

            <div style={{ padding: '16px 20px', borderTop: '1px solid var(--glass-border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', gap: 10 }}>
                <Btn
                  variant="ghost"
                  icon="chevron_left"
                  onClick={() => { setInvError(null); setInvIndex(i => Math.max(0, i - 1)); }}
                  disabled={invIndex === 0}
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  Назад
                </Btn>
                {isLast ? (
                  <Btn
                    variant="primary"
                    icon="check"
                    onClick={() => saveInventory(true)}
                    disabled={invSaving}
                    style={{ flex: 1, justifyContent: 'center' }}
                  >
                    {invSaving ? 'Сохранение...' : 'Завершить'}
                  </Btn>
                ) : (
                  <Btn
                    variant="primary"
                    icon="chevron_right"
                    onClick={() => { setInvError(null); setInvIndex(i => Math.min(invChips.length - 1, i + 1)); }}
                    style={{ flex: 1, justifyContent: 'center' }}
                  >
                    Далее
                  </Btn>
                )}
              </div>
              <Btn
                variant="subtle"
                icon="check"
                onClick={() => saveInventory(false)}
                disabled={invSaving}
                style={{ justifyContent: 'center' }}
              >
                {invSaving ? 'Сохранение...' : 'Сохранить и продолжить позже'}
              </Btn>
            </div>
          </>
        )}
      </div>
    );
  }

  if (view === 'form') return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header title={selected ? 'Редактировать' : 'Новый компонент'} onBack={() => setView(selected ? 'detail' : 'list')} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 100px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 520 }}>
          <Field label="Наименование" required error={String(errors.name || '')}>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Название" />
          </Field>
          <Field label="Описание">
            <AutocompleteInput
              value={form.description}
              onChange={v => setForm(f => ({ ...f, description: v }))}
              suggestions={descriptions}
              onDelete={deleteDescription}
              placeholder="Характеристики..."
            />
          </Field>
          <Field label="Корпус">
            <AutocompleteInput
              value={form.package}
              onChange={v => setForm(f => ({ ...f, package: v }))}
              suggestions={packages}
              onDelete={deletePackage}
              placeholder="DIP-8, SOIC-16..."
            />
          </Field>
          <Field label="Кол-во" error={errors.quantity || ''}>
            <input
              type="number"
              min="0"
              max="9999"
              step="1"
              value={form.quantity}
              onChange={e => {
                const v = e.target.value;
                // Блокируем дробные значения сразу при вводе
                if (v.includes('.') || v.includes(',')) return;
                setForm(f => ({ ...f, quantity: v }));
              }}
              onKeyDown={e => {
                if (e.key === ',' || e.key === '.') e.preventDefault();
              }}
              onPaste={e => {
                const text = e.clipboardData.getData('text');
                if (text.includes(',') || text.includes('.')) e.preventDefault();
              }}
            />
          </Field>
          <Field label="Позиция" error={errors.section || ''}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              <input
                value={form.section}
                onChange={e => { setForm(f => ({ ...f, section: e.target.value })); setErrors(er => ({ ...er, section: undefined })); }}
                placeholder="Секция"
                style={{
                  borderRadius: 'var(--r-sm) 0 0 var(--r-sm)', borderRight: 'none', flex: 1,
                  borderColor: errors.section ? 'var(--red)' : undefined,
                }}
              />
              <div style={{ width: 1, alignSelf: 'stretch', background: errors.section ? 'var(--red)' : 'var(--glass-border)', flexShrink: 0 }} />
              <input
                value={form.cell}
                onChange={e => { setForm(f => ({ ...f, cell: e.target.value })); setErrors(er => ({ ...er, section: undefined })); }}
                placeholder="Ячейка"
                style={{
                  borderRadius: '0 var(--r-sm) var(--r-sm) 0', borderLeft: 'none', flex: 1,
                  borderColor: errors.section ? 'var(--red)' : undefined,
                }}
              />
            </div>
          </Field>

          {/* Фото */}
          <Field label="Фото">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files?.[0];
                if (!file) return;
                setPhotoFile(file);
                setPhotoPreview(URL.createObjectURL(file));
              }}
            />
            {photoPreview ? (
              <div style={{ position: 'relative' }}>
                <img
                  src={photoPreview}
                  alt="preview"
                  style={{
                    width: '100%', maxHeight: 200, objectFit: 'contain',
                    borderRadius: 'var(--r-sm)',
                    border: '1px solid var(--glass-border)',
                    display: 'block',
                    background: 'var(--glass-bg)',
                  }}
                />
                <button
                  type="button"
                  onClick={() => { setPhotoFile(null); setPhotoPreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  style={{
                    position: 'absolute', top: 6, right: 6,
                    width: 28, height: 28, borderRadius: '50%',
                    background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(255,255,255,0.2)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Icon name="close" size={14} color="#fff" />
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    position: 'absolute', top: 6, left: 6,
                    background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 'var(--r-sm)', padding: '4px 10px',
                    cursor: 'pointer', color: '#fff', fontSize: 12, fontFamily: 'var(--sans)',
                  }}
                >
                  Заменить
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: '100%', padding: '20px 0',
                  borderRadius: 'var(--r-sm)',
                  border: '1px dashed var(--glass-border)',
                  background: 'var(--glass-bg)',
                  cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  color: 'var(--text2)',
                  fontFamily: 'var(--sans)', fontSize: 13,
                }}
              >
                <Icon name="camera" size={22} color="var(--text2)" />
                Сфотографировать или выбрать
              </button>
            )}
          </Field>
        </div>
      </div>
      <div style={{ padding: '16px 20px', borderTop: '1px solid var(--glass-border)', display: 'flex', gap: 10 }}>
        <Btn variant="primary" onClick={save} icon="check" disabled={saving} style={{ flex: 1, justifyContent: 'center' }}>
          {saving ? 'Сохранение...' : 'Сохранить'}
        </Btn>
        <Btn variant="ghost" onClick={() => setView(selected ? 'detail' : 'list')}>Отмена</Btn>
      </div>
    </div>
  );

  if (view === 'detail' && selected) return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {confirm && (
        <ConfirmDialog
          message={`Удалить «${selected.name}»?`}
          onConfirm={del}
          onCancel={() => setConfirm(false)}
        />
      )}
      <Header title="Компонент" onBack={() => setView('list')} />
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {selected.photo_path && (
          <PhotoSlider images={[selected.photo_path]} />
        )}
        {!selected.photo_path && (
          <div style={{
            width: '100%',
            height: 140,
            borderRadius: 'var(--r-md)',
            border: '1px dashed var(--glass-border-strong)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            marginBottom: 20,
            color: 'var(--text3)',
            background: 'var(--glass-bg)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}>
            <Icon name="zoom" size={26} color="var(--text3)" />
            <span style={{ fontSize: 12, fontFamily: 'var(--mono)' }}>фото компонента</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--mono)', letterSpacing: '-0.01em', color: 'var(--text0)' }}>
            {selected.name}
          </h2>
          {(selected.shadow || selected.is_shadowed === 1) && <Badge variant="shadow">Теневой</Badge>}
        </div>
        {selected.description && (
          <p style={{ color: 'var(--text1)', fontSize: 14, lineHeight: 1.65, marginBottom: 16 }}>{selected.description}</p>
        )}
        <InfoTable rows={[
          ['Корпус', selected.package || '—'],
          ['На складе', (
            <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 17, color: selected.quantity < 5 ? 'var(--red)' : 'var(--teal)' }}>
              {selected.quantity} шт.
            </span>
          )],
          ['Позиция', (() => {
            const isShadowed = selected.shadow || selected.is_shadowed === 1;
            const sec = isShadowed ? selected.shadow_section : selected.section;
            const cel = isShadowed ? selected.shadow_cell : selected.cell;
            if (!sec && !cel) return '—';
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <span style={{
                  fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 14,
                  background: isShadowed ? 'var(--amber-dim, rgba(251,191,36,0.1))' : 'var(--glass-bg)',
                  border: `1px solid ${isShadowed ? 'var(--amber, #fbbf24)' : 'var(--glass-border)'}`,
                  padding: '3px 10px', borderRadius: 'var(--r-sm) 0 0 var(--r-sm)',
                  borderRight: 'none', opacity: isShadowed ? 0.7 : 1,
                }}>{sec || '—'}</span>
                <span style={{ width: 1, background: isShadowed ? 'var(--amber, #fbbf24)' : 'var(--glass-border)', alignSelf: 'stretch', display: 'inline-block', opacity: isShadowed ? 0.7 : 1 }} />
                <span style={{
                  fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 14,
                  background: isShadowed ? 'var(--amber-dim, rgba(251,191,36,0.1))' : 'var(--glass-bg)',
                  border: `1px solid ${isShadowed ? 'var(--amber, #fbbf24)' : 'var(--glass-border)'}`,
                  padding: '3px 10px', borderRadius: '0 var(--r-sm) var(--r-sm) 0',
                  borderLeft: 'none', opacity: isShadowed ? 0.7 : 1,
                }}>{cel || '—'}</span>
              </div>
            );
          })()],
          ['Добавлен', formatDate(selected.created_at)],
        ]} />
        <div style={{ display: 'flex', gap: 10, marginTop: 16, marginBottom: 10 }}>
          <Btn variant="ghost" icon="minus" onClick={() => changeQty(-1)} style={{ flex: 1, justifyContent: 'center' }}>−1</Btn>
          <Btn variant="primary" icon="plus" onClick={() => changeQty(1)} style={{ flex: 1, justifyContent: 'center' }}>+1</Btn>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn variant="ghost" icon="edit" onClick={() => openEdit(selected)} style={{ flex: 1, justifyContent: 'center' }}>Изменить</Btn>
          <Btn variant="ghost" icon="move" onClick={toggleShadow} style={{ flex: 1, justifyContent: 'center' }}>
            {(selected.shadow || selected.is_shadowed === 1) ? 'Из тени' : 'В тень'}
          </Btn>
          <Btn variant="danger" icon="trash" onClick={() => setConfirm(true)}>Удалить</Btn>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header
        title="Чипы"
        action={
          <Btn variant="primary" icon="plus" onClick={openAdd} style={{ padding: '7px 14px' }}>
            Добавить
          </Btn>
        }
      />
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--glass-border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Компонент, корпус..." />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <PillBtn active={!showShadow} onClick={() => setShowShadow(false)}>Основной</PillBtn>
          <PillBtn active={showShadow} amber onClick={() => setShowShadow(true)}>Теневой список</PillBtn>
          <PillBtn active={false} onClick={openInventory}>Инвентаризация</PillBtn>

          {/* Фильтр по количеству */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginLeft: 'auto' }}>
            <button
              type="button"
              onClick={() => {
                if (lowQtyFilter !== null) {
                  setLowQtyFilter(null);
                } else {
                  const n = parseInt(lowQtyInput, 10);
                  setLowQtyFilter(isNaN(n) || n < 1 ? 5 : n);
                }
              }}
              style={{
                padding: '6px 12px',
                borderRadius: lowQtyFilter !== null ? 'var(--r-sm) 0 0 var(--r-sm)' : 'var(--r-sm)',
                border: '1px solid',
                fontSize: 12,
                fontFamily: 'var(--sans)',
                fontWeight: 500,
                cursor: 'pointer',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                background: lowQtyFilter !== null ? 'var(--red-dim)' : 'var(--glass-bg)',
                color: lowQtyFilter !== null ? 'var(--red)' : 'var(--text2)',
                borderColor: lowQtyFilter !== null ? 'var(--red-mid)' : 'var(--glass-border)',
                transition: 'all 0.15s',
                whiteSpace: 'nowrap',
              }}
            >
              {lowQtyFilter !== null ? `< ${lowQtyFilter} шт. ×` : '< N шт.'}
            </button>
            {lowQtyFilter === null && (
              <input
                type="number"
                min="1"
                max="999"
                value={lowQtyInput}
                onChange={e => setLowQtyInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const n = parseInt(lowQtyInput, 10);
                    setLowQtyFilter(isNaN(n) || n < 1 ? 5 : n);
                  }
                }}
                style={{
                  width: 52,
                  padding: '5px 8px',
                  borderRadius: '0 var(--r-sm) var(--r-sm) 0',
                  border: '1px solid var(--glass-border)',
                  borderLeft: 'none',
                  background: 'var(--glass-bg)',
                  color: 'var(--text0)',
                  fontSize: 13,
                  fontFamily: 'var(--mono)',
                  textAlign: 'center',
                }}
              />
            )}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text2)' }}>Загрузка...</div>
        ) : displayed.length === 0 ? (
          <EmptyState icon="chips" title="Компонентов нет" sub="Добавьте первый компонент" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {displayed.map(c => (
              <GlassCard key={c.id} onClick={() => { setSelected(c); setView('detail'); }} style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 14, marginBottom: 6, color: 'var(--text0)' }}>
                      {c.name}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {c.package && <Badge variant="default">{c.package}</Badge>}
                      {(c.section || c.cell) && (
                        <span style={{ color: 'var(--text2)', fontSize: 12, fontFamily: 'var(--mono)' }}>
                          {c.section}{c.section && c.cell && <span style={{ opacity: 0.4, margin: '0 3px' }}>|</span>}{c.cell}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 22, color: c.quantity < 5 ? 'var(--red)' : 'var(--teal)' }}>
                      {c.quantity}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>шт.</div>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChipsSection;
